import React, { createContext, useContext, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { flushSync } from "react-dom";
import useRouter from "use-react-router";

import { WizardStepId } from "../types";
import { SEGMENT_TO_STEP, WIZARD_STEPS, wizardStepPath } from "../urls";
import { WizardContextValue } from "./types";
import { initialWizardState, wizardReducer } from "./wizardReducer";
import { useWizardStorage } from "./wizardStorage";

const WizardContext = createContext<WizardContextValue | null>(null);

export const WizardProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { history, location } = useRouter();
  const {
    loading,
    persistedState,
    saveState,
    saveStateImmediate,
    cancelPendingSave,
    clearState,
  } = useWizardStorage();
  const [state, dispatch] = useReducer(wizardReducer, initialWizardState);
  const [isHydrated, setIsHydrated] = useState(false);
  const hasHydratedRef = useRef(false);
  const hasSkippedInitialPersistRef = useRef(false);
  const skipNextPersistRef = useRef(false);

  useEffect(() => {
    if (loading || hasHydratedRef.current) {
      return;
    }

    dispatch({ type: "HYDRATE", payload: persistedState ?? initialWizardState });
    hasHydratedRef.current = true;
    setIsHydrated(true);
  }, [loading, persistedState]);

  useEffect(() => {
    if (!isHydrated) {
      return;
    }

    if (skipNextPersistRef.current) {
      skipNextPersistRef.current = false;

      return;
    }

    if (!hasSkippedInitialPersistRef.current) {
      hasSkippedInitialPersistRef.current = true;

      return;
    }

    saveState(state);
  }, [saveState, state]);

  const currentStepId = useMemo<WizardStepId>(() => {
    const segment = location.pathname.split("/").pop() ?? "step-1";
    const step = SEGMENT_TO_STEP.get(segment);

    return step?.id ?? "company-information";
  }, [location.pathname]);

  const currentStepIndex = useMemo(
    () => WIZARD_STEPS.findIndex(s => s.id === currentStepId),
    [currentStepId],
  );

  // Step-order enforcement: redirect to the first incomplete step if the user
  // navigates directly to a step they haven't unlocked yet.
  useEffect(() => {
    if (currentStepIndex <= 0) {
      return;
    }

    const isStepDone = (id: WizardStepId) =>
      state.completedSteps.includes(id) || state.skippedSteps.includes(id);

    const previousStep = WIZARD_STEPS[currentStepIndex - 1];

    if (previousStep && !isStepDone(previousStep.id)) {
      // Find the first step that hasn't been completed or skipped
      const firstIncompleteIndex = WIZARD_STEPS.findIndex(s => !isStepDone(s.id));
      const targetStep = WIZARD_STEPS[Math.max(0, firstIncompleteIndex)];

      history.replace(wizardStepPath(targetStep.urlSegment));
    }
    // Only run on mount or when the URL changes, not when state updates
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStepIndex]);

  const goToNextStep = () => {
    const nextStep = WIZARD_STEPS[currentStepIndex + 1];

    if (nextStep) {
      history.push(wizardStepPath(nextStep.urlSegment));
    }
  };

  const goToPreviousStep = () => {
    const prevStep = WIZARD_STEPS[currentStepIndex - 1];

    if (prevStep) {
      history.push(wizardStepPath(prevStep.urlSegment));
    }
  };

  const skipCurrentStep = () => {
    flushSync(() => {
      dispatch({ type: "SKIP_STEP", stepId: currentStepId });
    });
    goToNextStep();
  };

  const completeCurrentStep = () => {
    flushSync(() => {
      dispatch({ type: "COMPLETE_STEP", stepId: currentStepId });
    });
  };

  const completeWizard = async () => {
    const completedState = {
      ...state,
      wizardCompleted: true,
    };

    flushSync(() => {
      dispatch({ type: "COMPLETE_WIZARD" });
    });

    cancelPendingSave();
    await saveStateImmediate(completedState);
    window.dispatchEvent(new CustomEvent("wizard-completed"));
    history.push("/analytics");
  };

  const resetWizardState = async () => {
    const resetState = {
      ...initialWizardState,
      startedAt: new Date().toISOString(),
    };

    cancelPendingSave();
    skipNextPersistRef.current = true;
    await clearState();

    flushSync(() => {
      dispatch({ type: "RESET", payload: resetState });
    });

    history.replace(wizardStepPath(WIZARD_STEPS[0].urlSegment));
  };

  const value: WizardContextValue = {
    currentStepId,
    completedSteps: state.completedSteps,
    skippedSteps: state.skippedSteps,
    wizardCompleted: state.wizardCompleted,
    isStepCompleted: id => state.completedSteps.includes(id),
    isStepSkipped: id => state.skippedSteps.includes(id),
    goToNextStep,
    goToPreviousStep,
    skipCurrentStep,
    completeCurrentStep,
    completeWizard,
    resetWizardState,
  };

  if (!isHydrated) {
    return null;
  }

  return <WizardContext.Provider value={value}>{children}</WizardContext.Provider>;
};

export const useWizardContext = (): WizardContextValue => {
  const context = useContext(WizardContext);

  if (context === null) {
    throw new Error("useWizardContext must be used within a WizardProvider");
  }

  return context;
};
